import React from 'react';
import { graphql } from 'react-relay';

import BaseLayout from '@zego/layouts/BaseLayout';
import withData from '@zego/lib/withData';
import { BREAKPOINTS, unit, white } from '@zego/theme';

import H1 from '@zego/components/atoms/H1';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import Button from '@zego/components/button/ButtonLink';
import ContentContainer from '@zego/components/ContentContainer';
import BroadcastBanner from '@zego/components/header/BroadcastBanner';
import Link from '@zego/components/Link';
import ProductCustomerService from '@zego/components/ProductCustomerService';
import {
  FeatureContent,
  LeftFeature,
  RightFeature,
} from '@zego/components/ProductFeatureBlock';
import ProductHero from '@zego/components/ProductHero';
import ProductPageAppDownload from '@zego/components/ProductPageAppDownload';
import ProductTheme from '@zego/components/ProductTheme';
import SectionDisplay from '@zego/components/SectionDisplay';
import { IECopy } from '@zego/components/SharedBannerContent';
import SubItemsSection from '@zego/components/SubItemsSection';

import heroImage from '@zego/static/images/hero-scooter.jpg';
import image3 from '@zego/static/images/ScooterCombineTwo.jpg';
import image1 from '@zego/static/images/ScooterPAYGInsurance.jpg';

const HOW_IT_WORKS_CONTENT = {
  title: 'How It Works',
  itemsTitleAlign: 'left',
  itemsDescriptionAlign: 'left',
  descriptionAlign: 'left',
  items: [
    {
      title: 'Select your insurance',
      numberHeader: '.01',
      description:
        'We offer pay-as-you-go hourly scooter insurance as well as 30 day policies.',
    },
    {
      title: 'Get a quote',
      numberHeader: '.02',
      description:
        'Get a quote in minutes on the Zego app. You can find us on the App Store and Google Play. Once you’ve downloaded it, simply select scooter insurance from the main menu.',
    },
    {
      title: "You're all set!",
      numberHeader: '.03',
      description:
        'Accept your quote, add your payment details and you’re ready to go! All that’s left is for you to head out on the road and start earning.',
    },
  ],
};

const ScooterIePage = ({ viewer }) => (
  <BaseLayout
    viewer={viewer}
    title="Zego Scooter Insurance | Delivery rider insurance. Hourly or 30 day
  policies."
    description="Scooter insurance for delivery riders. Available throughout Ireland, you can choose a cover that suits your needs. Policies range from an hour to a year."
    banner={
      <BroadcastBanner
        backgroundColor={white}
        text={<IECopy country={viewer.country} />}
        iconTheme="dark"
      />
    }
  >
    <ProductTheme product={viewer.product}>
      <>
        <ProductHero image={heroImage}>
          <ProductHero.Header>
            Flexible scooter insurance designed to fit the way you work.
          </ProductHero.Header>
          <ProductHero.SubHeader>
            With a choice of pay-as-you-go hourly and 30 day policies, you can
            find the cover that’s right for you.
          </ProductHero.SubHeader>
          <Button href="#download-app">Get the app</Button>
        </ProductHero>
        <Spacer height={unit(12)} />
        <SubItemsSection {...HOW_IT_WORKS_CONTENT} />
        <Spacer height={unit(12)} mobileHeight={unit(6)} />
        <SectionDisplay>
          <ContentContainer>
            <H1 css={{ maxWidth: 400 }}>
              Scooter insurance that suits the way you work
            </H1>
          </ContentContainer>
        </SectionDisplay>
        <LeftFeature image={image1}>
          <FeatureContent
            title="Pay-as-you-go hourly scooter insurance"
            description="With our pay-as-you-go hourly scooter insurance, you only pay for cover during your working hours. That means no more paying for your cover you don’t need."
            link={<Link href="/ie/scooter/pay-as-you-go">Find out more</Link>}
          />
        </LeftFeature>
        {/* <RightFeature fluidImage={data.image2}>
          <FeatureContent
            title="Annual scooter insurance"
            description="Always on the go? Our annual scooter insurance is perfect if you’re working full-time hours on your scooter. It’s one simple policy that covers your free time as well as your work."
            link={
              <ProductLink product={data.scooterAnnual}>
                Find out more
              </ProductLink>
            }
          />
        </RightFeature> */}
        <RightFeature image={image3}>
          <FeatureContent
            title="30 day scooter insurance"
            description="Our 30 day scooter insurance is ideal for the busy months. Covering your work and your free time, it’s perfect if you’re looking working full-time hours on a temporary basis."
            link={<Link href="/ie/scooter/30-day">Find out more</Link>}
          />
        </RightFeature>

        <View
          css={{
            paddingTop: unit(2),
            paddingBottom: unit(2),
            [BREAKPOINTS.medium]: {
              paddingTop: unit(10),
              paddingBottom: unit(10),
            },
          }}
        >
          {/* <RelatedProducts
            title={<span>Get the right insurance for you</span>}
            product={data.product}
          /> */}
        </View>

        <ProductPageAppDownload />
        <ProductCustomerService viewer={viewer} />
      </>
    </ProductTheme>
  </BaseLayout>
);

export default withData(ScooterIePage, {
  query: graphql`
    query ScooterIePageQuery($country: String!, $code: String!) {
      viewer {
        ...BaseLayout_viewer @arguments(country: $country)
        ...ProductCustomerService_viewer @arguments(country: $country)
        country: countryByCode(code: $country) {
          ...IECopy_country
        }
        product: websiteMarketingProductByCodeAndCountryCode(
          code: $code
          countryCode: $country
        ) {
          ...ProductTheme_product
        }
      }
    }
  `,
  getVariables: ({ query }) => {
    return {
      country: query.country,
      code: 'SCOOTER',
    };
  },
});
